import { useState } from 'react';
import { reorder } from '../../_events/_manage-event/_edit-website/website-form-body/helpers';

export type DropPlaceholderGeometry = {
    top: number;
    left: number;
    width: number;
    height: number;
};

export type ItemMetrics = {
    height: number;
    marginBottom: number;
};

export type UseDropPlaceholderResult = {
    placeholder: DropPlaceholderGeometry | null;
    syncPlaceholder: (draggableId: string, sourceIndex: number, destinationIndex?: number) => void;
    clearPlaceholder: () => void;
};

export const computePlaceholderGeometry = (
    items: ItemMetrics[],
    dragged: { width: number; height: number },
    padding: { top: number; left: number },
    sourceIndex: number,
    destinationIndex: number,
): DropPlaceholderGeometry => {
    const reordered = reorder(items, sourceIndex, destinationIndex);
    const top = reordered
        .slice(0, destinationIndex)
        .reduce((total, item) => total + item.height + item.marginBottom, padding.top);

    return {
        top: top,
        left: padding.left,
        width: dragged.width,
        height: dragged.height,
    };
};

const getDraggedDom = (draggableId: string): HTMLElement | null =>
    document.querySelector(`[data-rbd-draggable-id='${draggableId}']`);

const getItemMetrics = (parent: HTMLElement): ItemMetrics[] =>
    Array.from(parent.children)
        .filter((node): node is HTMLElement => node instanceof HTMLElement && node.classList.contains('drag-drop-list__item'))
        .map(element => ({ height: element.clientHeight, marginBottom: parseFloat(window.getComputedStyle(element).marginBottom) || 0 }));

const computePlaceholder = (draggableId: string, sourceIndex: number, destinationIndex: number): DropPlaceholderGeometry | null => {
    const draggedDom = getDraggedDom(draggableId);
    const parent = draggedDom?.parentElement;
    if (!draggedDom || !parent) {
        return null;
    }

    const parentStyle = window.getComputedStyle(parent);
    const padding = { top: parseFloat(parentStyle.paddingTop) || 0, left: parseFloat(parentStyle.paddingLeft) || 0 };
    const dragged = { width: draggedDom.clientWidth, height: draggedDom.clientHeight };

    return computePlaceholderGeometry(getItemMetrics(parent), dragged, padding, sourceIndex, destinationIndex);
};

export const useDropPlaceholder = (enabled: boolean): UseDropPlaceholderResult => {
    const [placeholder, setPlaceholder] = useState<DropPlaceholderGeometry | null>(null);

    const syncPlaceholder = (draggableId: string, sourceIndex: number, destinationIndex?: number): void => {
        if (!enabled || typeof destinationIndex !== 'number') {
            setPlaceholder(null);
            return;
        }
        setPlaceholder(computePlaceholder(draggableId, sourceIndex, destinationIndex));
    };

    const clearPlaceholder = (): void => setPlaceholder(null);

    return { placeholder: placeholder, syncPlaceholder: syncPlaceholder, clearPlaceholder: clearPlaceholder };
};
